/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    AlertIcon,
    AlertTitle,
    Box,
    Button,
    Center,
    Checkbox,
    Heading,
    HStack,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useDisclosure,
} from '@chakra-ui/react'
import { differenceInHours } from 'date-fns'

import { CollapsableToDrawer } from '@components/CollapsableToDrawer'
import { LineupsMetaData } from '@components/MatchMetaData/LineupsMetaData'

import { useCreateNewLineup } from '@hooks/useCreateNewLineup'
import { useMatchLineup } from '@hooks/useMatchLineup'
import { usePreviousLineup } from '@hooks/usePreviousLineup'
import { useSelectedMatchDetails } from '@hooks/useSelectedMatchDetails'

import { FixtureLineup, MetaData, StateEnum } from '@common/types'
import { deleteLineupFromCache, getLineupFromCache } from '@common/utils'

import { useLineupDataContext } from '@contexts/LineupDataContext'

import { useMissingPlayers } from '@queries/useMissingPlayers'

import { LineupEditContainer } from '../LineupAndSubsPreview/LineupEditModal/LineupEditContainer'
import { EarlySaveConfirmationDialog } from './EarlySaveConfirmationDialog'
import {
    checkAreAllPlayersIn,
    checkIsLineupWithoutFormation,
    processMatchLineupResponse,
    transformLineupData,
} from './utils/lineup'

export function LineupAndSubsPreviewTabs() {
    const selectedMatchDetails = useSelectedMatchDetails()

    const { matchLineup, isLoading } = useMatchLineup(selectedMatchDetails.id)
    const { missingPlayers } = useMissingPlayers(selectedMatchDetails.id)

    const { lineupData, setLineupData } = useLineupDataContext()
    const { postData, isLoading: isPostDataLoading, isError: isPostDataError } = useCreateNewLineup()
    const [isTeam1WithoutFormation, setIsTeam1WithoutFormation] = useState<boolean>(false)
    const [isTeam2WithoutFormation, setIsTeam2WithoutFormation] = useState<boolean>(false)
    const [isTeam1PreviousLineup, setIsTeam1PreviousLineup] = useState<boolean>(false)
    const [isTeam2PreviousLineup, setIsTeam2PreviousLineup] = useState<boolean>(false)
    const [initialLineupData, setInitialLineupData] = useState<FixtureLineup>()
    const [metaData, setMetaData] = useState<MetaData | null>(selectedMatchDetails.lineups_editor_meta)
    const [isPossibleLineups, setIsPossibleLineups] = useState<boolean>(
        Boolean(
            matchLineup?.team1_possible_lineup?.length &&
                !matchLineup?.team1_lineup?.length &&
                matchLineup.team2_possible_lineup?.length &&
                !matchLineup?.team2_lineup?.length,
        ),
    )

    const [initialIsPossibleLineups, setInitialIsPossibleLineups] = useState<boolean>()

    // Early lineup save confirmation dialog
    const {
        isOpen: isEarlyLineupDialogOpen,
        onOpen: onEarlyLineupDialogOpen,
        onClose: onEarlyLineupDialogClose,
    } = useDisclosure()

    const matchDate = useMemo(() => {
        if (!selectedMatchDetails?.date) {
            return null
        }

        return new Date(selectedMatchDetails.date)
    }, [selectedMatchDetails?.date])

    const isMoreThanTwoHoursBeforeMatch = useMemo(() => {
        if (!matchDate) {
            return false
        }

        return differenceInHours(matchDate, new Date()) > 2
    }, [matchDate])

    // Check if there's already a confirmed lineup saved for THIS match (notifications already sent)
    const hasExistingConfirmedLineup = useMemo(
        () => Boolean(matchLineup?.team1_lineup?.length || matchLineup?.team2_lineup?.length),
        [matchLineup?.team1_lineup?.length, matchLineup?.team2_lineup?.length],
    )

    const handleSaveLineup = async () => {
        const postDataResponse = await postData(lineupData)

        if (lineupData.fixture_id && !isPostDataError && postDataResponse) {
            deleteLineupFromCache(lineupData.fixture_id)

            setInitialIsPossibleLineups(lineupData.possible_lineups)

            setIsTeam1PreviousLineup(false)

            setIsTeam2PreviousLineup(false)
        }

        setMetaData({ ...metaData, ...{ state: StateEnum.IN_PROGRESS } })
    }

    const handleSaveButtonClick = () => {
        // Show confirmation dialog if:
        // - Saving as lineup (not possible lineup)
        // - More than 2 hours before match
        // - No existing confirmed lineup (notifications not yet sent)
        if (!isPossibleLineups && isMoreThanTwoHoursBeforeMatch && !hasExistingConfirmedLineup) {
            onEarlyLineupDialogOpen()

            return
        }

        handleSaveLineup()
    }

    const handleSaveAsPossibleLineup = async () => {
        onEarlyLineupDialogClose()

        // Update lineup data to save as possible lineup
        setLineupData(prev => ({ ...prev, possible_lineups: true }))

        setIsPossibleLineups(true)

        const postDataResponse = await postData({ ...lineupData, possible_lineups: true })

        if (lineupData.fixture_id && !isPostDataError && postDataResponse) {
            deleteLineupFromCache(lineupData.fixture_id)

            setInitialIsPossibleLineups(true)

            setIsTeam1PreviousLineup(false)

            setIsTeam2PreviousLineup(false)
        }

        setMetaData({ ...metaData, ...{ state: StateEnum.IN_PROGRESS } })
    }

    const handleSaveAsLineupAnyway = () => {
        onEarlyLineupDialogClose()

        handleSaveLineup()
    }

    const { previousLineup } = usePreviousLineup(selectedMatchDetails?.team1?.id, selectedMatchDetails?.team2?.id)

    useEffect(() => {
        const cachedLineup = getLineupFromCache(selectedMatchDetails?.id)
        const {
            team1,
            team2,
            isPossibleLineups: newIsPossibleLineups,
        } = processMatchLineupResponse(matchLineup, previousLineup)

        setIsPossibleLineups(newIsPossibleLineups)

        setIsTeam1PreviousLineup(team1.isPreviousLineup)

        setIsTeam2PreviousLineup(team2.isPreviousLineup)

        const team1TransformedLineup = cachedLineup ? cachedLineup.team1_lineup : transformLineupData(team1.lineup)

        const team2TransformedLineup = cachedLineup ? cachedLineup.team2_lineup : transformLineupData(team2.lineup)

        const team1LineupFormation = cachedLineup ? cachedLineup.team1_formation : team1.formation
        const team2LineupFormation = cachedLineup ? cachedLineup.team2_formation : team2.formation

        setLineupData(previousLineupData => ({
            ...previousLineupData,
            fixture_id: selectedMatchDetails?.id,
            status: selectedMatchDetails?.status ?? null,
            team1_id: selectedMatchDetails?.team1?.id,
            team2_id: selectedMatchDetails?.team2?.id,
            team1_formation: team1LineupFormation,
            team1_lineup: team1TransformedLineup,
            team2_formation: team2LineupFormation,
            team2_lineup: team2TransformedLineup,
            possible_lineups: isPossibleLineups,
        }))

        setInitialLineupData(previousLineupData => ({
            ...previousLineupData,
            fixture_id: selectedMatchDetails?.id,
            status: selectedMatchDetails?.status ?? null,
            team1_id: selectedMatchDetails?.team1?.id,
            team2_id: selectedMatchDetails?.team2?.id,
            team1_formation: team1.formation,
            team1_lineup: transformLineupData(team1.lineup),
            team2_formation: team2.formation,
            team2_lineup: transformLineupData(team2.lineup),
            possible_lineups: isPossibleLineups,
        }))

        setInitialIsPossibleLineups(isPossibleLineups)
    }, [selectedMatchDetails.id, matchLineup, previousLineup])

    useEffect(() => {
        setIsTeam1WithoutFormation(
            checkIsLineupWithoutFormation(matchLineup?.team1_lineup, matchLineup?.team1_formation),
        )

        setIsTeam2WithoutFormation(
            checkIsLineupWithoutFormation(matchLineup?.team2_lineup, matchLineup?.team2_formation),
        )
    }, [matchLineup])

    useEffect(() => {
        setMetaData(selectedMatchDetails?.lineups_editor_meta)
    }, [selectedMatchDetails])

    useEffect(() => {
        setLineupData(previousLineupData => ({ ...previousLineupData, possible_lineups: isPossibleLineups }))
    }, [isPossibleLineups, setLineupData])

    const fixture = useMemo(
        () => ({
            team1: { id: selectedMatchDetails?.team1?.id, name: selectedMatchDetails?.team1?.name },
            team2: { id: selectedMatchDetails?.team2?.id, name: selectedMatchDetails?.team2?.name },
        }),
        [selectedMatchDetails],
    )

    const isSaveButtonDisabled = useMemo(
        () => !(checkAreAllPlayersIn(lineupData?.team1_lineup) && checkAreAllPlayersIn(lineupData?.team2_lineup)),
        [lineupData?.team1_lineup, lineupData?.team2_lineup],
    )

    if (isLoading) {
        return (
            <Center>
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" m="10rem" />
            </Center>
        )
    }

    // If there's no team name, just write Home Button, else Home Button - Team Name
    const homeButtonTitle = selectedMatchDetails?.team1?.name ? `Home - ${selectedMatchDetails?.team1?.name}` : 'Home'

    const awayButtonTitle = selectedMatchDetails?.team2?.name ? `Away - ${selectedMatchDetails?.team2?.name}` : 'Away'

    const handleIsPossibleLineupsToggle = () => {
        setIsPossibleLineups(!isPossibleLineups)
    }

    if (selectedMatchDetails?.id) {
        return (
            <>
                <Box w="100%">
                    <Heading as="h2" size="md" mb={1} minH="10">
                        Meta Data
                    </Heading>
                    <Box
                        w="100%"
                        alignItems="start"
                        borderStyle="solid"
                        borderWidth="1px"
                        borderColor="blackAlpha.50"
                        borderRadius="16"
                        px={{ base: 4, xl: 8 }}
                        py={4}>
                        <CollapsableToDrawer>
                            <LineupsMetaData matchId={selectedMatchDetails?.id} metaData={metaData} />
                        </CollapsableToDrawer>
                    </Box>
                </Box>
                <HStack width="100%" justifyContent="center" alignItems="center" gap={3}>
                    <Checkbox
                        size="md"
                        isChecked={isPossibleLineups}
                        onChange={handleIsPossibleLineupsToggle}
                        className="possible-lineups-checkbox">
                        Possible lineups
                    </Checkbox>
                    <Button
                        colorScheme="teal"
                        isDisabled={isSaveButtonDisabled}
                        onClick={handleSaveButtonClick}
                        isLoading={isPostDataLoading}>
                        {isPossibleLineups ? 'Save Possible Lineups' : 'Save Lineups'}
                    </Button>
                </HStack>
                <Tabs isFitted variant="line" w="100%">
                    <TabList>
                        <Tab className="home-team-tab">{homeButtonTitle}</Tab>
                        <Tab className="away-team-tab">{awayButtonTitle}</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel p={{ base: 0, xl: 4 }} pt={4}>
                            <LineupEditContainer
                                isHomeTeam={true}
                                isLineupWithoutFormation={isTeam1WithoutFormation}
                                isPreviousLineup={isTeam1PreviousLineup}
                                initialLineup={initialLineupData}
                                fixture={fixture}
                                initialMissingPlayers={missingPlayers?.team1MissingPlayers}
                                isPossibleLineups={isPossibleLineups}
                                initialIsPossibleLineups={initialIsPossibleLineups}
                            />
                        </TabPanel>
                        <TabPanel p={{ base: 0, xl: 4 }} pt={4}>
                            <LineupEditContainer
                                isHomeTeam={false}
                                isLineupWithoutFormation={isTeam2WithoutFormation}
                                isPreviousLineup={isTeam2PreviousLineup}
                                initialLineup={initialLineupData}
                                fixture={fixture}
                                initialMissingPlayers={missingPlayers?.team2MissingPlayers}
                                isPossibleLineups={isPossibleLineups}
                                initialIsPossibleLineups={initialIsPossibleLineups}
                            />
                        </TabPanel>
                    </TabPanels>
                </Tabs>

                <EarlySaveConfirmationDialog
                    isOpen={isEarlyLineupDialogOpen}
                    onClose={onEarlyLineupDialogClose}
                    onSaveAsPossibleLineup={handleSaveAsPossibleLineup}
                    onSaveAsLineupAnyway={handleSaveAsLineupAnyway}
                    matchDate={matchDate}
                    isLoading={isPostDataLoading}
                />
            </>
        )
    }

    return (
        <Alert status="info">
            <AlertIcon />
            <AlertTitle>No match selected!</AlertTitle>
            Please select a match to continue.
        </Alert>
    )
}
